<!-- src/views/admin/AdminFinanceDashboardView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & GLOBAL COMMAND ROW -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 animate-fadeIn relative z-30 text-left border-x border-t border-slate-800">
          
          <div class="absolute inset-0 rounded-[40px] overflow-hidden pointer-events-none">
            <div class="absolute top-0 right-0 p-12 opacity-5">
              <BanknotesIcon class="h-32 w-32 text-white" />
            </div>
          </div>
          
          <div class="relative z-10">
            <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-8">
              <div>
                <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Financial Command Centre</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4">Registry Intelligence • Final Authorisation & Analytics</p>
              </div>
              
              <div class="flex flex-wrap items-center gap-3">
                <router-link v-slot="{ href, navigate }" v-if="hasFinanceClearance" to="/admin/manage-finance-form-options" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2 no-underline">
                    <AdjustmentsHorizontalIcon class="h-4 w-4" /> <span>Options</span>
                  </a>
                </router-link>

                <!-- 🛡️ NAVIGATION STANDARDISED: Updated button text to 'Return' and matched padding -->
                <router-link v-slot="{ href, navigate }" to="/client-admin" custom>
                  <a :href="href" @click="navigate" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 no-underline">
                    <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
                  </a>
                </router-link>
              </div>
            </div>

            <div class="bg-white/5 p-5 rounded-[32px] border border-white/10 relative">
              <div class="flex flex-wrap items-end gap-3">
                
                <!-- Search -->
                <div class="flex flex-col gap-1.5 flex-grow min-w-[180px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Search Personnel</span>
                  <input v-model="filters.search" type="text" placeholder="Name..." class="filter-input-dark h-[46px]" />
                </div>

                <!-- Registry Mode Selection -->
                <div class="flex flex-col gap-1.5 w-full sm:w-[160px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Registry Mode</span>
                  <select v-model="viewMode" class="filter-select-dark h-[46px]">
                    <option value="submitted">PENDING ({{ submittedSubmissions.length }})</option>
                    <option value="pending">AUTHORISED ({{ authorisedSubmissions.length }})</option>
                    <option value="queried">QUERIED ({{ queriedSubmissions.length }})</option>
                    <option value="declined">DECLINED ({{ declinedSubmissions.length }})</option>
                    <option value="archive">PROCESSED ({{ archiveSubmissions.length }})</option>
                  </select>
                </div>

                <!-- Dept Selection -->
                <div class="flex flex-col gap-1.5 w-full sm:w-[140px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Department</span>
                  <select v-model="filters.department" class="filter-select-dark h-[46px]">
                    <option value="all">All</option>
                    <option v-for="dept in availableDepartments" :key="dept" :value="dept">{{ formatDepartment(dept) }}</option>
                  </select>
                </div>

                <!-- Period Selection -->
                <div class="flex flex-col gap-1.5 w-full sm:w-[120px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Period</span>
                  <select v-model="filters.period" @change="fetchData" class="filter-select-dark h-[46px]">
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                    <option value="year">Fiscal</option>
                  </select>
                </div>

                <!-- Rail Weeks (Multi-Select) -->
                <div class="flex flex-col gap-1.5 relative w-full sm:w-[160px] z-[50]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Rail Weeks</span>
                  <div class="relative">
                    <button @click="isWeekDropdownOpen = !isWeekDropdownOpen" type="button" class="filter-select-dark h-[46px] w-full text-left flex justify-between items-center">
                      <span class="truncate pr-4">{{ filters.railWeeks.length === 0 ? 'All Weeks' : `${filters.railWeeks.length} Active` }}</span>
                    </button>
                    
                    <div v-if="isWeekDropdownOpen" class="absolute top-full left-0 right-0 mt-3 bg-[#1E293B] border border-white/20 rounded-2xl shadow-2xl z-[100] p-4 max-h-80 overflow-y-auto custom-scrollbar animate-fadeIn">
                      <div class="flex items-center justify-between mb-3 pb-2 border-b border-white/10">
                        <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest">Select</span>
                        <button @click="filters.railWeeks = []" class="text-[8px] font-black text-rose-400 uppercase tracking-widest hover:text-rose-300">Clear</button>
                      </div>
                      <div class="space-y-1">
                        <label v-for="week in availableRailWeeks" :key="week" class="flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg cursor-pointer transition-colors group/item">
                          <input type="checkbox" :value="week" v-model="filters.railWeeks" class="w-4 h-4 rounded border-white/20 bg-white/5 text-[#20FCF1] focus:ring-[#20FCF1]" />
                          <span class="text-[10px] font-black text-white uppercase tracking-tight group-hover/item:text-[#20FCF1]">Week {{ week }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Export Format Selection -->
                <div class="flex flex-col gap-1.5 w-full sm:w-[150px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Export Format</span>
                  <select v-model="exportMode" class="filter-select-dark h-[46px]">
                    <option value="detailed">DETAILED LOGS</option>
                    <option value="consolidated">PAYROLL (PIVOT)</option>
                  </select>
                </div>

                <!-- 🛡️ RE-STYLED EXPORT BUTTON: Styled exactly like the dark glass Training export button -->
                <button 
                  @click="exportToExcel" 
                  :disabled="exporting || currentDisplaySubmissions.length === 0" 
                  class="px-6 bg-white/10 border border-white/20 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 h-[46px] disabled:opacity-50 shadow-lg cursor-pointer border-none shrink-0"
                >
                  <template v-if="exporting">
                    <div class="animate-spin h-3 w-3 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
                    <span>Syncing...</span>
                  </template>
                  <template v-else>
                    <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Export Current List</span>
                  </template>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 🛡️ 2. SENTINEL INTELLIGENCE & INTEGRATED METRICS CARD -->
        <div class="mb-12 animate-fadeIn relative z-10 text-left">
          <div class="bg-white border-2 border-slate-100 rounded-[40px] shadow-2xl overflow-hidden relative">
            <div class="bg-[#0A1630] px-10 py-6 border-b-4 border-[#20FCF1] flex flex-col md:flex-row justify-between items-center gap-6">
               <div class="flex items-center gap-4">
                  <div class="bg-[#20FCF1]/10 p-3 rounded-2xl">
                    <SparklesIcon class="h-6 w-6 text-[#20FCF1]" />
                  </div>
                  <div>
                    <h2 class="text-xl font-black text-white uppercase tracking-tight leading-none">Sentinel Intelligence</h2>
                    <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mt-2">Active Multi-Form Synthesis • Deterministic Audit Logic</p>
                  </div>
               </div>
               
               <button 
                 @click="triggerSynthesis" 
                 :disabled="isAiLoading"
                 class="px-6 py-3 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg"
               >
                 <span v-if="isAiLoading" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                 <SignalIcon v-else class="h-4 w-4" />
                 <span>{{ isAiLoading ? 'Synthesising...' : 'Synchronise Intelligence' }}</span>
               </button>
            </div>

            <div class="p-8 md:p-12 text-left">
               <!-- 📊 INTEGRATED METRICS ROW inside Sentinel Intelligence Card -->
               <div class="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-b border-slate-100 pb-8 text-left animate-fadeIn">
                 <div v-for="stat in analyticsStats" :key="stat.label" class="flex items-center gap-4 text-left">
                   <div class="relative w-12 h-12 flex items-center justify-center text-center">
                     <svg class="absolute inset-0 w-full h-full" viewBox="0 0 24 24" fill="none">
                         <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" :fill="stat.color" fill-opacity="0.1" :stroke="stat.color" stroke-width="1.5"/>
                     </svg>
                     <component :is="stat.icon" class="w-5 h-5" :style="{ color: stat.color }" />
                   </div>
                   <div class="text-left">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-left">{{ stat.label }}</p>
                     <h4 class="text-xl font-black text-[#0A1630] leading-none text-left">{{ stat.value }}</h4>
                   </div>
                 </div>
               </div>

               <!-- Initial State -->
               <div v-if="!aiReport && !isAiLoading" class="flex flex-col items-center justify-center py-12 text-center">
                  <div class="bg-slate-50 p-6 rounded-full mb-6">
                    <CpuChipIcon class="h-10 w-10 text-slate-200" />
                  </div>
                  <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">Awaiting Initialisation</h3>
                  <p class="text-xs text-slate-400 font-bold mt-2 max-w-sm">Trigger a synchronisation to audit current registry submissions for patterns and risks.</p>
               </div>

               <!-- 🛡️ LOCAL SYNTHESIS CONTENT -->
               <div v-else-if="aiReport" class="grid grid-cols-1 lg:grid-cols-3 gap-10">
                  <div class="lg:col-span-2 space-y-8">
                     <div class="border-l-4 border-[#20FCF1] pl-6">
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-3">Executive Summary</h4>
                        <p class="text-sm font-medium text-slate-600 leading-relaxed italic">
                          "{{ aiReport.summary }}"
                        </p>
                     </div>

                     <div>
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Priority Interventions</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div v-for="(action, idx) in aiReport.actions" :key="idx" class="bg-slate-50 p-5 rounded-3xl border border-slate-100 flex flex-col justify-between">
                              <div>
                                <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest block mb-2">{{ action.type }}</span>
                                <p class="text-xs font-black text-[#0A1630] leading-tight mb-3 uppercase tracking-tight">{{ action.instruction }}</p>
                              </div>
                              <p class="text-[10px] text-slate-500 font-medium leading-relaxed">{{ action.reason }}</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="bg-[#0A1630]/5 p-8 rounded-[32px] border border-slate-100 h-fit">
                    <h4 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 border-b border-[#0A1630]/10 pb-4">Hotspot Trends</h4>
                    <div class="space-y-6">
                       <div v-for="(trend, idx) in aiReport.trends" :key="idx" class="flex items-start gap-4">
                          <div :class="trend.isCritical ? 'bg-rose-500' : 'bg-[#20FCF1]'" class="h-2 w-2 rounded-full shrink-0 mt-1.5 shadow-sm"></div>
                          <div>
                            <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-tight">{{ trend.label }}</p>
                            <p class="text-[11px] text-slate-500 font-medium mt-1 leading-tight">{{ trend.value }}</p>
                          </div>
                       </div>
                    </div>
                    <div class="mt-8 pt-6 border-t border-[#0A1630]/10 text-[9px] font-black text-slate-400 uppercase">
                      Analysis Depth: Last 50 Records
                    </div>
                  </div>
               </div>

               <!-- Loading State -->
               <div v-if="isAiLoading" class="flex flex-col items-center justify-center py-12">
                  <div class="animate-spin h-12 w-12 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
                  <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Aggregating Cross-Form Data & Synthesising Findings...</p>
               </div>
            </div>
          </div>
        </div>

        <!-- 3. DATA REGISTRY -->
        <div v-if="loading" class="text-center py-24">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Hydrating Financial Registry...</p>
        </div>

        <div v-else-if="paginatedSubmissions.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 shadow-inner animate-fadeIn">
           <MagnifyingGlassIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight text-center">Registry Neutral</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2 text-center">No records identified for the selected criteria.</p>
        </div>

        <div v-else class="space-y-6 animate-fadeIn relative z-0 text-left">
          <div v-for="sub in paginatedSubmissions" :key="sub.id" class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl transition-all overflow-hidden group">
            
            <div class="p-6 md:p-8">
              <div class="flex flex-col xl:flex-row items-center justify-between gap-8">
                <div class="flex items-center gap-6 w-full xl:w-[35%] shrink-0 text-left">
                  <div class="relative h-16 w-16 shrink-0">
                    <img v-if="sub.userPhoto" :src="sub.userPhoto" class="h-16 w-16 rounded-2xl object-cover border border-slate-100 shadow-sm" alt="Personnel" />
                    <div v-else class="h-16 w-16 bg-[#0A1630] rounded-2xl flex items-center justify-center font-black text-[#20FCF1] text-xl border-b-2 border-[#20FCF1]">
                      {{ (getVal(sub, 'userName') || 'U').charAt(0) }}
                    </div>
                  </div>
                  <div class="min-w-0 flex flex-col text-left">
                    <div class="flex items-baseline gap-3 mb-1">
                      <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight leading-none text-left">{{ getVal(sub, 'userName') }}</h3>
                    </div>
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest bg-[#EBFDF8] px-2 py-0.5 rounded-md">Week {{ getVal(sub, 'railWeek') }}</span>
                      <span class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter truncate">{{ getVal(sub, 'department') }}</span>
                    </div>
                  </div>
                </div>

                <!-- STATS AREA -->
                <div class="flex-grow px-8 border-l border-slate-50 min-w-0 flex items-center justify-around gap-8">
                   <div class="text-center">
                      <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1">Total Hours</p>
                      <p class="text-xl font-black text-[#0A1630] leading-none">{{ getVal(sub, 'totalHours') || 0 }}</p>
                   </div>
                   <div class="text-center">
                      <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest mb-1">Est. Basic Pay</p>
                      <p class="text-xl font-black text-[#0A1630] leading-none">£{{ calculateBasePay(sub) }}</p>
                   </div>
                </div>

                <!-- COMMAND ACTIONS -->
                <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-3 text-left">
                  <button @click="toggleExpand(sub.id)" class="px-6 py-4 bg-slate-50 text-slate-500 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#0A1630] hover:text-white transition-all flex items-center gap-2">
                    <MagnifyingGlassIcon class="h-4 w-4" />
                    <span>{{ expandedId === sub.id ? 'Hide Data' : 'Review' }}</span>
                  </button>

                  <template v-if="viewMode === 'pending'">
                    <button @click="openAuthorisationModal(sub)" class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg active:scale-95 text-center">
                      Process Payment
                    </button>
                  </template>

                  <div v-else-if="viewMode === 'archive'" class="bg-[#EBFDF8] text-[#20FCF1] px-5 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest border border-[#20FCF1]/20 flex items-center gap-2">
                    <CheckBadgeIcon class="h-3.5 w-3.5" /> Paid
                  </div>

                  <!-- 🛡️ INTERCEPTION PORTAL -->
                  <div v-else-if="viewMode === 'submitted'" class="flex items-center gap-3">
                    <router-link 
                      v-slot="{ href, navigate }"
                      v-if="canViewAllFinance"
                      :to="{ path: '/timesheets/review', query: { id: sub.id }}"
                      custom
                    >
                      <a :href="href" @click="navigate" class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all flex items-center gap-2 no-underline">
                        <ShieldCheckIcon class="h-4 w-4 text-[#20FCF1]" /> Sign Off Record
                      </a>
                    </router-link>
                    <div v-else class="px-6 py-4 bg-amber-50 text-amber-600 rounded-2xl text-[9px] font-black uppercase tracking-widest border border-amber-100 flex items-center gap-2">
                      <ClockIcon class="h-3.5 w-3.5 text-[#20FCF1]" /> Awaiting Management
                    </div>
                  </div>
                  
                  <button v-if="userStore.isAdmin" @click="initialiseVoid(sub)" title="Void Record" class="p-4 bg-slate-100 text-slate-400 rounded-2xl hover:bg-rose-500 hover:text-white transition-all shadow-sm cursor-pointer border-none">
                    <ArchiveBoxXMarkIcon class="h-5 w-5" />
                  </button>
                </div>
              </div>

              <!-- 🛡️ AUDIT METADATA FOOTER -->
              <div class="mt-8 pt-6 border-t border-slate-50 flex flex-wrap items-center gap-x-12 gap-y-4">
                <div class="flex items-center gap-2">
                  <ClockIcon class="h-3.5 w-3.5 text-slate-300" />
                  <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Handshake Initiated:</span>
                  <span class="text-[10px] font-bold text-[#0A1630] uppercase">{{ formatDate(getVal(sub, 'submittedAt')) }}</span>
                </div>
                
                <div class="flex items-center gap-2">
                  <ShieldCheckIcon class="h-3.5 w-3.5 text-slate-300" />
                  <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Protocol Status:</span>
                  <span :class="statusColorClass(getVal(sub, 'status'))" class="text-[10px] font-black uppercase">{{ getVal(sub, 'status') }}</span>
                </div>

                <div v-if="getVal(sub, 'managerAuthorisedBy')" class="flex items-center gap-2">
                  <UserCircleIcon class="h-3.5 w-3.5 text-[#20FCF1]" />
                  <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Manager Authorisation:</span>
                  <span class="text-[10px] font-black text-[#0A1630] uppercase">{{ getVal(sub, 'managerAuthorisedBy') }}</span>
                  <span class="text-slate-300 mx-1">•</span>
                  <span class="text-[10px] font-bold text-slate-400 uppercase">{{ formatDate(getVal(sub, 'managerAuthorisedAt')) }}</span>
                </div>
              </div>
            </div>

            <!-- EXPANDABLE BREAKDOWN -->
            <Transition 
              enter-active-class="transition duration-300 ease-out" 
              enter-from-class="transform opacity-0 -translate-y-4" 
              enter-to-class="transform opacity-100 translate-y-0" 
              leave-active-class="transition duration-200 ease-in" 
              leave-from-class="transform opacity-100 translate-y-0" 
              leave-to-class="transform opacity-0 -translate-y-4"
            >
              <div v-if="expandedId === sub.id" class="bg-slate-50 p-6 sm:p-10 border-t border-slate-100 text-left">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
                  <div v-for="day in (getVal(sub, 'days') || [])" :key="day.isoDate" class="bg-white p-5 rounded-[24px] border border-slate-200 shadow-sm flex flex-col text-left min-w-0">
                    <p class="text-[9px] font-black text-slate-400 uppercase mb-4 tracking-[0.2em] border-b border-slate-50 pb-2">{{ day.dayName }}</p>
                    <div v-if="!day.isOff && day.entries?.length" class="space-y-6 flex-grow text-left">
                      <div v-for="(entry, eIdx) in day.entries" :key="eIdx" class="border-l-2 border-[#20FCF1] pl-3 text-left">
                        
                        <!-- Header: Hours & Job Ref Stacked -->
                        <div class="flex flex-col gap-1 text-left mb-3">
                           <p class="text-2xl font-black text-[#0A1630] leading-none">{{ entry.hours }}<span class="text-[8px] text-slate-300 ml-0.5 font-bold">H</span></p>
                           <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest break-all leading-tight">{{ entry.jobNumber || 'NO REF' }}</p>
                        </div>

                        <!-- SECTOR VISIBILITY -->
                        <p class="text-[7px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-4">Sector: {{ entry.department || 'General' }}</p>

                        <!-- TASK DESCRIPTION VISIBILITY -->
                        <div v-if="entry.taskDescription" class="mt-4 pt-3 border-t border-slate-50">
                           <p class="text-[7px] font-black text-slate-300 uppercase tracking-[0.2em] mb-1.5">Task Description</p>
                           <p class="text-[9px] font-medium text-slate-600 leading-relaxed italic bg-slate-50 p-2.5 rounded-xl border border-slate-100 break-words">
                             "{{ entry.taskDescription }}"
                           </p>
                        </div>
                      </div>
                    </div>
                    <div v-else class="flex-grow flex items-center justify-center opacity-10 py-8"><MoonIcon class="h-6 w-6 text-[#0A1630]" /></div>
                  </div>
                </div>
              </div>
            </Transition>

          </div>
        </div>

        <!-- Symmetrical Pagination Controls (Forced h-11 heights) -->
        <div v-if="totalPages > 1 || itemsPerPage !== 25" class="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12 no-print">
          <div class="flex items-center gap-4">
            <button 
              @click="prevPage" 
              :disabled="currentPage === 1"
              class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
            >
              &larr; Prev
            </button>
            <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest">
              Page {{ currentPage }} of {{ totalPages || 1 }}
            </span>
            <button 
              @click="nextPage" 
              :disabled="currentPage === totalPages || totalPages === 0"
              class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
            >
              Next &rarr;
            </button>
          </div>
          
          <!-- Symmetrical Items Per Page Selector (Forced h-11 height) -->
          <div class="flex items-center gap-3">
            <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Display:</span>
            <select 
              v-model="itemsPerPage" 
              class="h-11 bg-[#0A1630] text-white rounded-xl px-5 text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer shadow-md appearance-none pr-8 text-center border-none"
              style="background-image: url('data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2320FCF1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 0.75rem;"
            >
              <option :value="25">25 Rows</option>
              <option :value="50">50 Rows</option>
              <option :value="100">100 Rows</option>
            </select>
          </div>
        </div>

      </div>
    </div>

    <!-- Settlement Modal -->
    <div v-if="showModal && activeSub" class="fixed inset-0 z-[200] flex items-center justify-center bg-[#0A1630]/90 backdrop-blur-md p-4 animate-fadeIn">
       <div class="bg-white rounded-[40px] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative border border-white/20 text-left">
        <div class="sticky top-0 bg-white z-10 p-8 border-b border-slate-100 flex justify-between items-center shadow-sm text-left">
           <div>
             <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none text-left">Financial Settlement Protocol</h3>
             <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-3 text-left">Personnel: {{ getVal(activeSub, 'userName') }} • Rail Week {{ getVal(activeSub, 'railWeek') }}</p>
           </div>
           <button @click="showModal = false" class="text-slate-300 hover:text-rose-500 transition-colors bg-slate-50 p-2 rounded-xl text-center cursor-pointer border-none"><XCircleIcon class="h-8 w-8" /></button>
        </div>
        <form @submit.prevent="finaliseAuthorisation" class="p-8 sm:p-10 space-y-12 text-left">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div class="md:col-span-3 flex items-center justify-between border-b border-slate-50 pb-4 text-left">
              <h4 class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] text-left">Hours & Overtime Registry</h4>
            </div>
            <div class="text-left"><label class="form-label text-left">Overtime Hours</label><input type="number" v-model.number="adjustments.overtimeHours" class="form-input text-left" step="0.5" /></div>
            <div class="text-left"><label class="form-label text-left">OT Rate p/h (£)</label><input type="number" v-model.number="adjustments.overtimeRatePH" class="form-input text-left" step="0.01" /></div>
            <div class="text-left"><label class="form-label text-left">Xmas Bonus</label><input type="number" v-model.number="adjustments.xmasBonus" class="form-input text-left" step="0.01" /></div>
          </div>
          <div class="pt-10 flex flex-col sm:flex-row gap-4 border-t border-slate-50 text-left">
             <button type="button" @click="showModal = false" class="flex-1 py-5 text-[11px] font-black uppercase text-slate-400 tracking-widest hover:text-slate-600 transition-colors text-center cursor-pointer bg-transparent border-none">Discard</button>
             <button type="submit" :disabled="isProcessing" class="flex-[2] bg-[#20FCF1] text-[#0A1630] py-5 rounded-[24px] font-black uppercase text-[11px] tracking-widest shadow-2xl hover:scale-105 active:scale-95 transition-all text-center cursor-pointer border-none">Record Payment & Archive</button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, watchEffect } from 'vue';
import { db, auth } from '@/firebaseConfig';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, addDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import * as XLSX from 'xlsx';
import { 
  BanknotesIcon, ArrowUturnLeftIcon, 
  ClockIcon, UserGroupIcon, ChartBarIcon, 
  CheckBadgeIcon, TableCellsIcon, AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon, MoonIcon, ArchiveBoxXMarkIcon, 
  ShieldCheckIcon, UserCircleIcon, XCircleIcon,
  SparklesIcon, CpuChipIcon, SignalIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();

const loading = ref(true);
const isProcessing = ref(false);
const showModal = ref(false);
const exporting = ref(false);
const isWeekDropdownOpen = ref(false);

const submittedSubmissions = ref([]); 
const authorisedSubmissions = ref([]); 
const queriedSubmissions = ref([]);
const declinedSubmissions = ref([]);
const archiveSubmissions = ref([]); 
const userRates = reactive({});
const activeSub = ref(null); 
const expandedId = ref(null);

// PAGINATION STATE (Defaulting to 25 rows with forced h-11 logic)
const currentPage = ref(1);
const itemsPerPage = ref(25);

// 🛡️ STICKY SELECTIONS & FILTERS: Read from localStorage dynamically
const viewMode = ref(localStorage.getItem('finance_sticky_mode') || 'submitted'); 
const exportMode = ref(localStorage.getItem('finance_sticky_export_mode') || 'detailed');

const filters = reactive({ 
  search: localStorage.getItem('finance_sticky_search') || '', 
  department: localStorage.getItem('finance_sticky_department') || 'all', 
  railWeeks: JSON.parse(localStorage.getItem('finance_sticky_rail_weeks') || '[]'), 
  period: localStorage.getItem('finance_sticky_period') || 'month' 
});

const adjustments = reactive({ overtimeHours: 0, overtimeRatePH: 0, xmasBonus: 0 });

// 🛡️ SYNTHESIS STATE REGISTRY
const aiReport = ref(null);
const isAiLoading = ref(false);

// 🛡️ STICKY WATCHERS: Write filter selections to localStorage dynamically
watch(viewMode, (val) => localStorage.setItem('finance_sticky_mode', val));
watch(exportMode, (val) => localStorage.setItem('finance_sticky_export_mode', val));
watch(() => filters.search, (val) => localStorage.setItem('finance_sticky_search', val));
watch(() => filters.department, (val) => localStorage.setItem('finance_sticky_department', val));
watch(() => filters.period, (val) => localStorage.setItem('finance_sticky_period', val));
watch(() => filters.railWeeks, (val) => localStorage.setItem('finance_sticky_rail_weeks', JSON.stringify(val)), { deep: true });

// Reset page back to 1 on tab/filter updates
watch([viewMode, exportMode, () => filters.search, () => filters.department, () => filters.period, () => filters.railWeeks, itemsPerPage], () => {
  currentPage.value = 1;
});

const getVal = (sub, key) => sub?.formData?.[key] ?? sub?.[key] ?? null;

const safeNormalize = (val) => {
  if (val === undefined || val === null) return '';
  return String(val).toLowerCase().trim();
};

const formatDepartment = (dept) => {
  if (!dept) return '';
  return dept
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

const statusColorClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('pending')) return 'text-amber-600';
  if (s.includes('authorised')) return 'text-[#20FCF1]';
  if (s.includes('declined')) return 'text-rose-500';
  if (s.includes('queried')) return 'text-indigo-600';
  if (s.includes('paid')) return 'text-[#20FCF1]';
  return 'text-slate-500';
};

const toggleExpand = (id) => { expandedId.value = expandedId.value === id ? null : id; };

const calculateBasePay = (sub) => {
  const uid = sub?.userId;
  const rate = userRates[uid]?.basic || 0;
  const hours = Number(getVal(sub, 'totalHours')) || 0;
  return (hours * rate).toFixed(2);
};

const analyticsStats = computed(() => {
  const filtered = currentDisplaySubmissions.value;
  let totalWages = 0, totalHours = 0;
  filtered.forEach(sub => {
    const uid = sub.userId;
    const rate = userRates[uid]?.basic || 0;
    totalWages += (Number(getVal(sub, 'totalHours')) || 0) * rate;
    totalHours += Number(getVal(sub, 'totalHours') || 0);
  });
  return [
    { label: 'Total Wages', value: `£${totalWages.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, color: '#20FCF1', icon: BanknotesIcon },
    { label: 'Total Man-Hours', value: totalHours.toFixed(1), color: '#6366F1', icon: ChartBarIcon },
    { label: 'Headcount', value: [...new Set(filtered.map(s => s.userId))].length, color: '#FBBF24', icon: UserGroupIcon },
    { label: 'Registry Total', value: filtered.length, color: '#0A1630', icon: CheckBadgeIcon },
  ];
});

const availableDepartments = computed(() => {
  const all = [...submittedSubmissions.value, ...authorisedSubmissions.value, ...archiveSubmissions.value];
  const rawDepts = all.map(s => getVal(s, 'department')).filter(Boolean);
  const uniqueDepts = [...new Set(rawDepts.map(d => safeNormalize(d)))];
  return uniqueDepts.sort();
});

const availableRailWeeks = computed(() => {
  const all = [...submittedSubmissions.value, ...authorisedSubmissions.value, ...archiveSubmissions.value];
  return [...new Set(all.map(s => getVal(s, 'railWeek')))].filter(w => w !== null).sort((a, b) => b - a);
});

const currentDisplaySubmissions = computed(() => {
  let source = [];
  if (viewMode.value === 'submitted') source = submittedSubmissions.value;
  else if (viewMode.value === 'pending') source = authorisedSubmissions.value;
  else if (viewMode.value === 'queried') source = queriedSubmissions.value;
  else if (viewMode.value === 'declined') source = declinedSubmissions.value;
  else source = archiveSubmissions.value;

  return source.filter(s => {
    const searchLow = filters.search.toLowerCase();
    const matchSearch = !filters.search || (getVal(s, 'userName') || '').toLowerCase().includes(searchLow);
    const matchDept = filters.department === 'all' || safeNormalize(getVal(s, 'department')) === safeNormalize(filters.department);
    const subWeek = getVal(s, 'railWeek');
    const matchWeek = filters.railWeeks.length === 0 || filters.railWeeks.includes(subWeek) || filters.railWeeks.includes(String(subWeek));
    return matchSearch && matchDept && matchWeek;
  });
});

// PAGINATION MATH
const totalPages = computed(() => {
  return Math.ceil(currentDisplaySubmissions.value.length / itemsPerPage.value);
});

const paginatedSubmissions = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return currentDisplaySubmissions.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

// 🛡️ UNCONDITIONAL PLATFORM AUTHORITY OVERRIDE: Simplifies permissions so all Command Access Admins can manage everything
const hasFinanceClearance = computed(() => {
  return userStore.role === 'super-admin' || 
         userStore.isAdmin || 
         userStore.hasAdminAccess ||
         userStore.permissions?.canViewFinance === true ||
         userStore.permissions?.canManageFinance === true;
});

const isSuperAdmin = computed(() => userStore.role === 'super-admin');
const isFullAdmin = computed(() => userStore.role === 'admin');

const canViewAllFinance = computed(() => {
  return isSuperAdmin.value || 
         isFullAdmin.value || 
         userStore.permissions?.canViewFinance === true || 
         userStore.userProfile?.permissions?.canViewFinance === true;
});

const fetchLatestAiReport = async (cid) => {
  try {
    const docSnap = await getDoc(doc(db, 'clients', cid, 'financial_intelligence', 'latest'));
    if (docSnap.exists()) {
      aiReport.value = docSnap.data();
    }
  } catch (err) {
    console.warn("Financial Intelligence Registry Neutral.");
  }
};

const fetchData = async () => {
  loading.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) return;

    await Promise.all([
      fetchLatestAiReport(cid)
    ]);

    const qAll = query(collection(db, 'timesheetSubmissions'), where('clientId', '==', cid));
    const snap = await getDocs(qAll);
    
    const profile = userStore.userProfile;
    const myDepts = (profile?.departments || []).map(d => safeNormalize(d)).filter(Boolean);
    const uniqueMyDepts = [...new Set(myDepts)];
    
    const hasFinanceAccess = canViewAllFinance.value;

    const allLogs = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(log => {
        if (hasFinanceAccess) return true;
        
        const subDept = safeNormalize(log.formData?.department || log.department);
        return uniqueMyDepts.includes(subDept);
    });

    const checkStatus = (log, match) => {
       const s = (log.status || log.formData?.status || '').toLowerCase();
       return s.includes(match.toLowerCase());
    };

    submittedSubmissions.value = allLogs.filter(l => checkStatus(l, 'Pending') || checkStatus(l, 'Approval'));
    authorisedSubmissions.value = allLogs.filter(l => (l.status === 'Authorised' || l.formData?.status === 'Authorised'));
    queriedSubmissions.value = allLogs.filter(l => checkStatus(l, 'Queried'));
    declinedSubmissions.value = allLogs.filter(l => checkStatus(l, 'Declined'));
    archiveSubmissions.value = allLogs.filter(l => (l.status === 'Paid' || l.formData?.status === 'Paid'));

    const uids = [...new Set(allLogs.map(s => s.userId))];
    await Promise.all(uids.map(async (uid) => {
      const pSnap = await getDoc(doc(db, 'userProfiles', uid));
      if (pSnap.exists()) {
        const data = pSnap.data();
        userRates[uid] = { basic: Number(data.basicHourlyRate) || 0, otPH: Number(data.overtimeHourlyRate) || 0 };
        allLogs.filter(s => s.userId === uid).forEach(m => m.userPhoto = data.photoUrl);
      }
    }));
  } catch (err) { console.error("Fetch Error:", err); } finally { loading.value = false; }
};

const getRefVal = (obj) => {
  if (!obj) return null;
  if (obj.multiselectRef) {
    if (typeof obj.multiselectRef === 'object') {
      if (obj.multiselectRef.text) return obj.multiselectRef.text;
      if (obj.multiselectRef.value) return obj.multiselectRef.value;
    } else if (typeof obj.multiselectRef === 'string') {
      return obj.multiselectRef;
    }
  }
  if (obj.jobNumber) return obj.jobNumber;
  if (obj.jobReference) return obj.jobReference;
  return null;
};

const getDayJobRef = (sub, dayName) => {
  const days = getVal(sub, 'days') || [];
  const day = days.find(d => d && d.dayName && d.dayName.toLowerCase().trim() === dayName.toLowerCase().trim());
  if (!day) return 'N/A';
  if (day.isOff) return 'Off';

  const directRef = getRefVal(day);
  if (directRef && directRef !== 'N/A') return directRef;

  if (day.entries && day.entries.length > 0) {
    const refs = day.entries.map(e => getRefVal(e)).filter(r => r && r !== 'N/A');
    if (refs.length > 0) return refs.join(', ');
  }

  return 'N/A';
};

const getWeeklyContractSelection = (sub) => {
  const fields = [
    'weeklyContractSelection',
    'weeklyContractOrJobRef',
    'weeklyContract',
    'weeklyContractJobRef',
    'jobRefSelection',
    'contractSelection',
    'multiselectRef'
  ];

  for (const field of fields) {
    const val = getVal(sub, field);
    if (val) {
      if (typeof val === 'object') {
        if (val.text) return val.text;
        if (val.value) return val.value;
      } else if (typeof val === 'string') {
        return val;
      }
    }
  }
  return 'N/A';
};

const getSubmissionJobHours = (sub, jobMap) => {
  const days = getVal(sub, 'days') || [];
  days.forEach(day => {
    if (day.isOff) return;
    
    if (day.entries && day.entries.length > 0) {
      day.entries.forEach(entry => {
        const hours = Number(entry.hours) || 0;
        if (hours > 0) {
          let job = getRefVal(entry);
          if (!job) job = getWeeklyContractSelection(sub);
          job = safeNormalize(job || 'Unspecified');
          jobMap[job] = (jobMap[job] || 0) + hours;
        }
      });
    } else {
      const hours = Number(day.hours) || 0;
      if (hours > 0) {
        let job = getRefVal(day);
        if (!job) job = getWeeklyContractSelection(sub);
        job = safeNormalize(job || 'Unspecified');
        jobMap[job] = (jobMap[job] || 0) + hours;
      }
    }
  });
};

const aggregatePayrollData = (submissions) => {
  const groups = {}; 
  const allJobsSet = new Set();

  submissions.forEach(sub => {
    const userName = getVal(sub, 'userName') || 'Unknown';
    const dept = getVal(sub, 'department') || 'General';
    const week = getVal(sub, 'railWeek');
    const key = `${userName}|${dept}`;

    if (!groups[key]) {
      groups[key] = {
        userName,
        department: dept,
        totalHours: 0,
        weeks: new Set(),
        jobs: {}
      };
    }

    if (week !== null && week !== undefined) {
      groups[key].weeks.add(week);
    }

    const jobMap = {};
    getSubmissionJobHours(sub, jobMap);

    Object.keys(jobMap).forEach(job => {
      allJobsSet.add(job);
      groups[key].jobs[job] = (groups[key].jobs[job] || 0) + jobMap[job];
      groups[key].totalHours += jobMap[job];
    });
  });

  const uniqueJobs = Array.from(allJobsSet).sort();

  const rows = Object.values(groups).map(group => {
    const weeksArray = Array.from(group.weeks).map(Number).sort((a, b) => a - b);
    const weeksString = weeksArray.length > 0 ? weeksArray.join(', ') : 'N/A';

    const row = {
      'Personnel': group.userName,
      'Department': formatDepartment(group.department),
      'Week(s)': weeksString,
      'Total Period Hours': group.totalHours
    };

    uniqueJobs.forEach(job => {
      row[job] = group.jobs[job] || 0;
    });

    return row;
  });

  return rows;
};

const appendSummaries = (ws, submissions) => {
  const weekSummary = {}; 
  const deptSummary = {}; 
  const weekDeptSummary = {}; 

  submissions.forEach(sub => {
    const week = getVal(sub, 'railWeek') || 'N/A';
    const dept = getVal(sub, 'department') || 'General';
    const hours = Number(getVal(sub, 'totalHours')) || 0;

    weekSummary[week] = (weekSummary[week] || 0) + hours;
    deptSummary[dept] = (deptSummary[dept] || 0) + hours;
    
    const key = `${week}|${dept}`;
    weekDeptSummary[key] = (weekDeptSummary[key] || 0) + hours;
  });

  const sortedWeeks = Object.keys(weekSummary).sort((a,b) => Number(a) - Number(b));
  const sortedDepts = Object.keys(deptSummary).sort();
  const sortedWeekDepts = Object.keys(weekDeptSummary).sort((a, b) => {
    const [wA, dA] = a.split('|');
    const [wB, dB] = b.split('|');
    if (wA !== wB) return Number(wA) - Number(wB);
    return dA.localeCompare(dB);
  });

  const summaryRows = [];

  summaryRows.push([]);
  summaryRows.push(['=== SYSTEM SUMMARY INTELLIGENCE REPORT ===']);
  summaryRows.push([]);

  summaryRows.push(['METRIC 1: TOTAL HOURS BY RAIL WEEK']);
  summaryRows.push(['Rail Week', 'Total Period Hours']);
  sortedWeeks.forEach(w => {
    summaryRows.push([w === 'N/A' ? 'N/A' : `Week ${w}`, weekSummary[w]]);
  });
  summaryRows.push([]);

  summaryRows.push(['METRIC 2: TOTAL HOURS BY DEPARTMENT']);
  summaryRows.push(['Department', 'Total Period Hours']);
  sortedDepts.forEach(d => {
    summaryRows.push([formatDepartment(d), deptSummary[d]]);
  });
  summaryRows.push([]);

  summaryRows.push(['METRIC 3: TOTAL HOURS BY WEEK & DEPARTMENT']);
  summaryRows.push(['Rail Week', 'Department', 'Total Period Hours']);
  sortedWeekDepts.forEach(wd => {
    const [week, dept] = wd.split('|');
    summaryRows.push([week === 'N/A' ? 'N/A' : `Week ${week}`, formatDepartment(dept), weekDeptSummary[wd]]);
  });

  XLSX.utils.sheet_add_aoa(ws, summaryRows, { origin: -1 });
};

const exportToExcel = () => {
  exporting.value = true;
  try {
    let dataToExport = [];
    if (exportMode.value === 'consolidated') {
      dataToExport = aggregatePayrollData(currentDisplaySubmissions.value);
    } else {
      dataToExport = currentDisplaySubmissions.value.map(sub => ({
        Personnel: getVal(sub, 'userName'),
        Dept: formatDepartment(getVal(sub, 'department')),
        Week: getVal(sub, 'railWeek'),
        Hours: getVal(sub, 'totalHours'),
        'Weekly Contract / Job Ref Selection': getWeeklyContractSelection(sub),
        'Sat Job Reference': getDayJobRef(sub, 'Saturday'),
        'Sun Job Reference': getDayJobRef(sub, 'Sunday'),
        'Mon Job Reference': getDayJobRef(sub, 'Monday'),
        'Tue Job Reference': getDayJobRef(sub, 'Tuesday'),
        'Wed Job Reference': getDayJobRef(sub, 'Wednesday'),
        'Thu Job Reference': getDayJobRef(sub, 'Thursday'),
        'Fri Job Reference': getDayJobRef(sub, 'Friday'),
        Pay: calculateBasePay(sub),
        Status: sub.status || sub.formData?.status || 'N/A'
      }));
    }
    const ws = XLSX.utils.json_to_sheet(dataToExport);

    const colWidths = Object.keys(dataToExport[0] ?? {}).map(key => {
      const maxLen = Math.max(
        key.length,
        ...dataToExport.map(row => String(row[key] ?? '').length)
      );
      return { wch: maxLen + 3 };
    });
    ws['!cols'] = colWidths;

    appendSummaries(ws, currentDisplaySubmissions.value);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, exportMode.value === 'consolidated' ? "Pivoted_Payroll" : "Payroll");
    XLSX.writeFile(wb, exportMode.value === 'consolidated' ? `Sentinel_Pivoted_Payroll_Export.xlsx` : `Sentinel_Finance_Export.xlsx`);
  } catch (err) { 
    console.error(err);
    alert("Export failure: " + err.message); 
  } finally { exporting.value = false; }
};

const triggerSynthesis = async () => {
    isAiLoading.value = true;
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) { alert("Identity context missing."); return; }
    
    await new Promise(r => setTimeout(r, 1200));

    try {
        const submissions = currentDisplaySubmissions.value;
        if (submissions.length === 0) {
            alert("No active records available to synthesise.");
            isAiLoading.value = false;
            return;
        }

        let totalWages = 0;
        let totalHours = 0;
        const deptHours = {};
        let highOvertimeAlerts = 0;

        submissions.forEach(sub => {
            const hours = Number(getVal(sub, 'totalHours')) || 0;
            const rate = userRates[sub.userId]?.basic || 0;
            totalWages += hours * rate;
            totalHours += hours;

            const dept = formatDepartment(getVal(sub, 'department') || 'General');
            deptHours[dept] = (deptHours[dept] || 0) + hours;

            if (hours > 50) {
                highOvertimeAlerts++;
            }
        });

        const headcount = [...new Set(submissions.map(s => s.userId))].length;
        
        let topDept = 'General';
        let maxHours = 0;
        Object.keys(deptHours).forEach(d => {
            if (deptHours[d] > maxHours) {
                maxHours = deptHours[d];
                topDept = d;
            }
        });

        const report = {
            summary: `Financial audit of active registry identifies a stable payroll distribution across ${submissions.length} timesheets representing an active headcount of ${headcount} personnel. Total man-hours accumulated stand at ${totalHours.toFixed(1)} with estimated basic wages of £${totalWages.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}. Primary labour deployment sector: ${topDept}.`,
            trends: [
                { label: 'Payroll Liability', value: `£${totalWages.toLocaleString(undefined, {maximumFractionDigits: 0})} basic pay`, isCritical: totalWages > 10000 },
                { label: 'Resource Velocity', value: `${totalHours.toFixed(1)} Man-Hours`, isCritical: false },
                { label: 'Peak Labour Sector', value: topDept, isCritical: true }
            ],
            actions: [
                { type: 'Payroll Check', instruction: `Verify ${highOvertimeAlerts} high-hour thresholds`, reason: 'Operatives exceeding standard weekly thresholds.' },
                { type: 'Final Sign-off', instruction: `Process payments for ${submissions.length} logs`, reason: 'Pending final authorisation workflow.' }
            ],
            createdAt: new Date().toISOString()
        };

        const reportRef = doc(db, 'clients', cid, 'financial_intelligence', 'latest');
        await setDoc(reportRef, { ...report, clientId: cid }, { merge: true });
        aiReport.value = report;

    } catch (e) {
        console.error("Synthesis Protocol Failure:", e);
        alert("Registry Handshake Failure: Insufficient authorisation to save intelligence log.");
    } finally {
        isAiLoading.value = false;
    }
};

const formatDate = (val) => {
  if (!val) return 'N/A';
  const d = val.toDate ? val.toDate() : (val instanceof Date ? val : new Date(val));
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const openAuthorisationModal = (sub) => { 
  activeSub.value = sub; 
  adjustments.overtimeHours = 0;
  adjustments.overtimeRatePH = userRates[sub.userId]?.otPH || 0;
  adjustments.xmasBonus = 0;
  showModal.value = true; 
};

const initialiseVoid = async (sub) => { if (confirm('Void record?')) { await updateDoc(doc(db, 'timesheetSubmissions', sub.id), { status: 'Voided', 'formData.status': 'Voided' }); fetchData(); } };

const finaliseAuthorisation = async () => {
  if (!activeSub.value || !auth.currentUser) return;
  isProcessing.value = true;
  try {
    const docRef = doc(db, 'timesheetSubmissions', activeSub.value.id);
    await updateDoc(docRef, { ...adjustments, status: 'Paid', 'formData.status': 'Paid' });
    
    await addDoc(collection(db, 'user_actions'), {
        targetUserId: activeSub.value.userId, title: 'Payment Processed', 
        description: `Log for Rail Week ${getVal(activeSub.value, 'railWeek')} paid.`,
        priority: 'Medium', status: 'Pending', category: 'Finance', 
        createdAt: serverTimestamp(), createdBy: auth.currentUser.uid
    });

    await fetchData();
    showModal.value = false;
  } catch (err) { console.error("Sentinel Intelligence Sync Error:", err); } finally { isProcessing.value = false; }
};

watchEffect(() => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  if (cid && userStore.isAuthReady) fetchData();
});
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-4 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500 shadow-inner; }
.filter-select-dark {
  @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-4 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2338E3B0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 1rem center; background-size: 1rem;
}
.form-input { @apply block w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-[#0A1630] shadow-sm focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all; }
.form-label { @apply block text-[10px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2 ml-1; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #20FCF1; border-radius: 10px; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>